import {
  actionSetLaborTickets,
  actionSetRecentOperatorRuns,
  actionSetCurrentWorkOrderOp,
} from 'lib/actions';

/**
 * Extracts labor tickets from NATS response (both actionResults and erpResponses)
 * @param {Object} response - The startActivityWithLaborTicketResponse
 * @returns {Object[]} Array of labor tickets
 */
const extractLaborTickets = (response) => {
  const { actionResults, erpResponses } = response || {};
  const laborTickets = [];

  if (actionResults) {
    const laborTicketData =
      actionResults.createLaborTicket ||
      actionResults.updateLaborTicket ||
      actionResults.openLaborTicket ||
      actionResults.closeLaborTicket;

    if (laborTicketData) {
      laborTickets.push(laborTicketData);
    }
  }

  if (erpResponses && Array.isArray(erpResponses)) {
    erpResponses.forEach((erpResponse) => {
      const laborTicket = erpResponse?.response?.laborTicket;
      if (laborTicket) {
        laborTickets.push(laborTicket);
      }
    });
  }

  return laborTickets;
};

/**
 * Transforms work order operation from labor ticket for Redux store
 * @param {Object} workOrderOperation - The work order operation from labor ticket
 * @returns {Object} Transformed work order operation
 */
const transformWorkOrderOperation = (workOrderOperation) => {
  if (!workOrderOperation) return null;

  const partOperation = workOrderOperation.matchingPartOperations?.[0];
  const partOperationCycleTimeMs = partOperation?.cycleTimeMs || 0;

  const getQuantityPerPart = (operation = {}) => {
    const { quantityPerPart = 1 } = operation;
    return !quantityPerPart || quantityPerPart <= 0 ? 1 : quantityPerPart;
  };

  const quantityPerPart = getQuantityPerPart(partOperation);

  return {
    ...workOrderOperation,
    quantityPerPart,
    partOperation,
    partOperationRef: partOperation?.partOperationRef,
    cycleTimeMs: workOrderOperation.cycleTimeMs || partOperationCycleTimeMs,
  };
};

/**
 * Handles NATS labor ticket response and dispatches appropriate actions
 * @param {Object} options - Handler options
 * @param {Object} options.response - The startActivityWithLaborTicketResponse
 * @param {Function} options.dispatch - Redux dispatch function
 * @param {Object} options.machine - Current machine from Redux state
 * @param {Object} options.options - Additional options
 * @param {boolean} options.options.handleOperatorRun - Whether to handle startOperatorRun
 * @param {boolean} options.options.handleWorkOrderOp - Whether to handle work order operation
 */
export const handleNatsLaborTicketResponse = ({
  response,
  dispatch,
  machine,
  options = {},
}) => {
  const { handleOperatorRun = false, handleWorkOrderOp = false } = options;
  const { actionResults } = response || {};

  // Extract and dispatch labor tickets
  const laborTickets = extractLaborTickets(response);
  if (laborTickets.length > 0) {
    dispatch(actionSetLaborTickets(laborTickets));
  }

  // Handle operator run if requested
  if (handleOperatorRun && actionResults?.startOperatorRun) {
    const { startOperatorRun } = actionResults;
    const { operator, ...operatorRun } = startOperatorRun;

    const mappedPayload = {
      operatorRuns: [
        {
          ...operatorRun,
          machineId: machine?.id,
          operatorId: operator?.id,
        },
      ],
    };
    dispatch(actionSetRecentOperatorRuns(mappedPayload));
  }

  // Handle work order operation if requested
  if (handleWorkOrderOp && laborTickets.length > 0) {
    const primaryLaborTicket = laborTickets[0];
    if (
      primaryLaborTicket?.state === 'OPEN' &&
      primaryLaborTicket?.workOrderOperation
    ) {
      const transformedWorkOrderOp = transformWorkOrderOperation(
        primaryLaborTicket.workOrderOperation
      );
      if (transformedWorkOrderOp) {
        dispatch(actionSetCurrentWorkOrderOp(transformedWorkOrderOp));
      }
    }
  }

  return { laborTickets };
};
